import moment from "moment";
import { DEVICE_TYPES as T, typeOf } from "../../../constants/devices";
import { actionKind, choiceOf } from "../../../automation/actions";
import { daysText, minutesText, sunText, timeText } from "../../../automation/format";

// The create flow's working copy of an automation, and turning it into what
// the API takes (app/screens/Automation/create/draft.js).

export const EVERY_DAY = [0, 1, 2, 3, 4, 5, 6];
export const WEEKDAYS = [1, 2, 3, 4, 5];

// types that take "only if" conditions, and types that can notify (the ones that run on their own)
export const CONDITION_TYPES = ["rule", "schedule", "sun", "location"];
export const NOTIFY_TYPES = ["schedule", "timer", "sun", "rule", "location", "inching", "cycling"];

// What a sensor's event means in a rule: door sensors report isClosed, the others isPresent
export function sensorEvents(device) {
  switch (typeOf(device)) {
    case T.DW_SENSOR:
      return [
        { key: "opens", label: "Opens", fact: "isClosed", value: false },
        { key: "closes", label: "Closes", fact: "isClosed", value: true },
      ];
    case T.PIR_SENSOR:
      return [{ key: "motion", label: "Detects motion", fact: "isPresent", value: true }];
    case T.GAS_SENSOR:
      return [{ key: "gas", label: "Detects gas", fact: "isPresent", value: true }];
    case T.SMOKE_DETECTOR:
      return [{ key: "smoke", label: "Detects smoke", fact: "isPresent", value: true }];
    default:
      return [];
  }
}

export function defaultTrigger(type, applianceId) {
  switch (type) {
    case "schedule":
      return { time: "07:00", days: WEEKDAYS };
    case "sun":
      return { event: "sunset", offsetMinutes: 0, days: EVERY_DAY };
    case "timer":
      return { delayMinutes: 60 };
    case "rule":
      return { applianceId: applianceId || null, fact: null, value: null };
    case "inching":
      return { applianceId, seconds: 60 };
    case "cycling":
      return { applianceId, onMinutes: 10, offMinutes: 20 };
    case "location":
      return { event: "arrive" };
    default:
      return {};
  }
}

export function newDraft(type, applianceId) {
  return {
    id: null,
    type: type || null,
    name: "",
    // false while the name is still the suggested one, so it follows later changes to the trigger
    nameTyped: false,
    trigger: defaultTrigger(type, applianceId),
    conditions: { window: null, days: null, devices: [] },
    // applianceId → { choice, level }, in the order they were added
    actions: [],
    // off | failures | always; the person who last sets it gets the notifications
    notify: NOTIFY_TYPES.includes(type) ? "failures" : "off",
    notifyChanged: false,
    notifyTo: null,
  };
}

// An automation from the API, back into the flow for editing
export function draftOf(automation, homeDevice) {
  const draft = newDraft(automation.type);
  draft.id = automation.id;
  draft.name = automation.name;
  draft.nameTyped = true;
  draft.notify = automation.notify || "off";
  draft.notifyTo = automation.notifyTo || null;
  const trigger = automation.trigger || {};
  if (automation.type === "timer") {
    const minutes = trigger.runAt ? Math.round(moment(trigger.runAt).diff(moment(), "minutes", true)) : 0;
    draft.trigger = { delayMinutes: minutes > 0 ? minutes : 60 };
  } else {
    draft.trigger = { ...draft.trigger, ...trigger };
  }
  (automation.conditions?.all || []).forEach((condition) => {
    if (condition.fact === "timerange" && condition.value) {
      draft.conditions.window = { from: condition.value.from, to: condition.value.to };
    } else if (condition.fact === "day" && Array.isArray(condition.value)) {
      draft.conditions.days = condition.value;
    } else if (condition.applianceId) {
      draft.conditions.devices.push({ applianceId: condition.applianceId, fact: condition.fact, value: condition.value });
    }
  });
  draft.actions = (automation.actions || []).map((action) => ({
    applianceId: action.applianceId,
    ...(action.state === "toggle" ? { choice: "toggle" } : choiceOf(action, homeDevice(action.applianceId))),
  }));
  return draft;
}

function triggerBody(draft) {
  const trigger = draft.trigger;
  switch (draft.type) {
    case "schedule":
      return { time: trigger.time, days: trigger.days };
    case "sun":
      return { event: trigger.event, offsetMinutes: trigger.offsetMinutes, days: trigger.days };
    case "timer":
      return { delaySeconds: trigger.delayMinutes * 60 };
    case "rule":
      return { applianceId: trigger.applianceId, fact: trigger.fact, operator: "equal", value: trigger.value };
    case "inching":
      return { applianceId: trigger.applianceId, seconds: trigger.seconds };
    case "cycling":
      return { applianceId: trigger.applianceId, onMinutes: trigger.onMinutes, offMinutes: trigger.offMinutes };
    case "location":
      // the person is whoever saves it, and the circle is the API's 200 m
      return { event: trigger.event };
    default:
      return {};
  }
}

function conditionsBody(draft) {
  const { window, days, devices } = draft.conditions;
  const all = [];
  if (window) all.push({ fact: "timerange", operator: "in", value: { from: window.from, to: window.to } });
  if (days) all.push({ fact: "day", operator: "in", value: days });
  devices.forEach((c) => all.push({ applianceId: c.applianceId, fact: c.fact, operator: "equal", value: c.value }));
  return { all, any: [] };
}

// The body for POST /automation/:homeId or PUT /automation/:homeId/:id
export function requestOf(draft, homeDevice) {
  const body = { name: draft.name.trim(), trigger: triggerBody(draft) };
  if (!draft.id) body.type = draft.type;
  // empty conditions clear them when editing
  if (CONDITION_TYPES.includes(draft.type)) body.conditions = conditionsBody(draft);
  // sending notify makes this person the one notified, so an edit sends it only when it changed
  if (NOTIFY_TYPES.includes(draft.type) && (!draft.id || draft.notifyChanged)) body.notify = draft.notify;
  if (!["inching", "cycling"].includes(draft.type)) {
    body.actions = draft.actions.map(({ applianceId, choice, level }) => {
      if (choice === "toggle") return { applianceId, state: "toggle" };
      const kind = actionKind(homeDevice(applianceId));
      return { applianceId, ...(kind ? kind.build(choice, level) : { state: choice }) };
    });
  }
  return body;
}

// A first name for it, which the person can change
export function suggestedName(draft, lookup) {
  const trigger = draft.trigger;
  const first = draft.actions[0] ? lookup.name(draft.actions[0].applianceId) : "";
  const more = draft.actions.length > 1 ? ` +${draft.actions.length - 1}` : "";
  let name;
  switch (draft.type) {
    case "schedule":
      name = `${first}${more} · ${daysText(trigger.days)} ${timeText(trigger.time)}`;
      break;
    case "sun":
      name = `${first}${more} · ${sunText(trigger).toLowerCase()}`;
      break;
    case "timer":
      name = `${first}${more} in ${minutesText(trigger.delayMinutes)}`;
      break;
    case "rule": {
      const event = sensorEvents(lookup.device(trigger.applianceId)).find(
        (e) => e.fact === trigger.fact && e.value === trigger.value
      );
      name = `When ${lookup.name(trigger.applianceId)} ${(event?.label || "changes").toLowerCase()}`;
      break;
    }
    case "inching":
      name = `${lookup.name(trigger.applianceId)} auto-off`;
      break;
    case "cycling":
      name = `${lookup.name(trigger.applianceId)} cycling`;
      break;
    case "location":
      name = `${first}${more} when I ${trigger.event === "leave" ? "leave" : "arrive"}`;
      break;
    default:
      name = first ? `${first}${more}` : "";
  }
  return name.trim().slice(0, 60);
}

// What's missing before the person can go on from a step; null when nothing is
export function missing(step, draft, home) {
  const trigger = draft.trigger;
  switch (step) {
    case "trigger":
      return draft.type ? null : "Pick what starts it.";
    case "details":
      if (["schedule", "sun"].includes(draft.type) && !trigger.days?.length) return "Pick at least one day.";
      if (["sun", "location"].includes(draft.type) && !home?.location) return "Set the home's location first.";
      if (draft.type === "rule" && !trigger.applianceId) return "Pick the sensor.";
      if (draft.type === "rule" && !trigger.fact) return "Pick what it should react to.";
      if (draft.type === "timer" && !(trigger.delayMinutes > 0)) return "Pick when it runs.";
      return null;
    case "conditions":
      if (draft.conditions.window && draft.conditions.window.from === draft.conditions.window.to) {
        return "The time window must start and end at different times.";
      }
      if (draft.conditions.days && !draft.conditions.days.length) return "Pick at least one day.";
      return null;
    case "actions":
      return draft.actions.length ? null : "Add at least one device.";
    case "save":
      return draft.name.trim() ? null : "Give it a name.";
    default:
      return null;
  }
}
